import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcRaglV1TransactionQueryServiceTransactionGet } from '@interxfi/api-client/generated/hooks/ixc-ragl-v1-transaction-query-service-transaction-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcRaglV1TransactionState } from '@interxfi/types/generated/api';
import { RaglTransactionDeltasTable } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-deltas-table';
import { RaglTransactionEntityMenu } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-entity-menu';
import { RaglTransactionEventSummary } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-event';
import { getRaglTransactionStateListItems } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-list-items';
import { getRaglTransactionDetailPath } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-routing';
import { RaglTransactionStatusPill } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-status-pill';
import { RaglTransactionSummaryBar } from '../../../../../../../../../features/clearing-ledger/transaction/ragl-transaction-summary-bar';
import { AccountHolderAvatarStack } from '../../../../../../../../../features/registration/account/account-holder-avatar-stack';
import { getTransactionTypeDetailPageItems } from '../../../../../../../../../features/transaction-shared/transaction-type-detail-page-items';
import { TransactionTypePill } from '../../../../../../../../../features/transaction-shared/transaction-type-pill';
import { getAccountIdForTransactionDetail } from '../../../../../../../../../features/transaction-shared/utils';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/transaction/_transaction/$transactionId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ragl-transaction-${params.transactionId}`,
        label: 'Transaction',
        to: getRaglTransactionDetailPath(params.transactionId),
        suffix: (
          <CopyButton
            content={params.transactionId}
            label="Copy Transaction ID"
            tooltip={{ copy: params.transactionId, copied: 'Copied Transaction ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Transaction'),
  component: RaglTransactionDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load transaction', 'view this transaction');

function getListItems(transaction: IxcRaglV1TransactionState | undefined) {
  const transactionItems = getRaglTransactionStateListItems(transaction);
  const base = getTransactionTypeDetailPageItems(transactionItems.data.detail, transaction?.data?.detail);
  const { left, right } = base.general;

  return {
    left: [
      ...left,
      ...(transactionItems.data.portfolio
        ? [
            {
              id: 'portfolio',
              title: 'Portfolio',
              items: [
                transactionItems.data.portfolio.accountId,
                transactionItems.data.portfolio.transactionId,
                transactionItems.data.portfolio.event,
              ],
            },
          ]
        : []),
      ...(transactionItems.data.reverses
        ? [
            {
              id: 'reverses',
              title: 'Reverses',
              items: [
                ...(transactionItems.data.reverses.transactionId ? [transactionItems.data.reverses.transactionId] : []),
                ...(transactionItems.data.reverses.reason ? [transactionItems.data.reverses.reason] : []),
              ],
            },
          ]
        : []),
      ...(transactionItems.data.reversedBy
        ? [
            {
              id: 'reversedBy',
              title: 'Reversed By',
              items: [
                ...(transactionItems.data.reversedBy.transactionId ? [transactionItems.data.reversedBy.transactionId] : []),
                ...(transactionItems.data.reversedBy.reason ? [transactionItems.data.reversedBy.reason] : []),
              ],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [
      transactionItems.metadata.createdAt,
      transactionItems.metadata.updatedAt,
      transactionItems.data.effectiveAt,
      transactionItems.transactionId,
      ...(transactionItems.data.settledByTransactionId ? [transactionItems.data.settledByTransactionId] : []),
      ...right,
    ] as const satisfies ReadonlyListItem[],
    additionalSectionsForTransactionType: base.sections,
  } as const;
}

function RaglTransactionDetail() {
  const { transactionId } = Route.useParams();
  const request = { transactionId } as const;
  const { data, error, isPending } = useIxcRaglV1TransactionQueryServiceTransactionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.transaction), [data?.transaction]);
  const accountIdForTransaction = getAccountIdForTransactionDetail(data?.transaction?.data?.detail);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <RaglTransactionStatusPill isPending={showPending} size="large" status={data?.transaction?.status} />
              <TransactionTypePill detail={data?.transaction?.data?.detail} isPending={showPending} size="large" source="ragl" />
              {accountIdForTransaction ? <AccountHolderAvatarStack accountId={accountIdForTransaction} /> : null}
            </HorizontalStack>

            <RaglTransactionEntityMenu transactionId={transactionId} />
          </PageHeader>

          <RaglTransactionSummaryBar transactionId={transactionId} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <RaglTransactionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {listItems.additionalSectionsForTransactionType?.map((section) => (
        <PageLayout.Section key={section.id}>{section.content}</PageLayout.Section>
      ))}

      <PageLayout.Section>
        <VerticalStack gap={7}>
          <Typography.Level3>Deltas</Typography.Level3>

          <RaglTransactionDeltasTable assets={data?.transaction?.data?.assets} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
